import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import CallTasksTab from './CallTasksTab';
import CopyTab from './CopyTab';
import EventsTab from './EventsTab';
import JourneysTab from './JourneysTab';
import { LifecycleProvider } from './LifecycleContext';
import Overview from './Overview';
import { LifecycleToastProvider } from './Overview/Toast';
import { C, FONT, Icon, PATHS, Plain } from './Overview/ui';
import { LifecycleTab, useLifecycleUrlState } from './useLifecycleUrlState';
import useNavigationGuard from './useNavigationGuard';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import SectionErrorBoundary from '@/routes/Agency/WorkerV2/components/SectionErrorBoundary';
import ROUTES from '@/routes/routes';
import { lifecycleAPI } from '@/services/lifecycleAPI';
import { trackLifecycleTabViewed, trackLifecycleViewed } from '@/util/vexo';

// The design shows one page. Everything the page had before (the call task
// queue, system events, the full message editor and the engine settings with
// the per-journey controls) stays one click away in these quiet links.
const SECONDARY: { key: LifecycleTab; label: string }[] = [
  { key: 'call-tasks', label: 'All call tasks' },
  { key: 'events', label: 'System events' },
  { key: 'copy', label: 'Full message editor' },
  { key: 'settings', label: 'Engine settings' },
];

const LABELS: Record<LifecycleTab, string> = {
  journeys: 'lifecycle journeys',
  'call-tasks': 'call tasks',
  copy: 'message copy',
  events: 'system events',
  settings: 'engine settings',
};

const Lifecycle = () => {
  const { tab, setTab } = useLifecycleUrlState();
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [copyDirty, setCopyDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<LifecycleTab | null>(null);
  const [countVersion, setCountVersion] = useState(0);
  // Sidebar and other in-app links. Browser Back is not covered (see hook).
  const leave = useNavigationGuard(copyDirty);
  const navigate = useNavigate();
  const location = useLocation();
  const viewed = useRef(false);

  // One page view per visit (with how the admin got here), then one event per
  // tab shown, the first included. The GravyWorkers button marks its link.
  useEffect(() => {
    if (!viewed.current) {
      viewed.current = true;
      const fromList =
        (location.state as { lifecycleEntry?: string } | null)
          ?.lifecycleEntry === 'gravyworkers';
      trackLifecycleViewed({
        entry: fromList ? 'gravyworkers' : 'direct',
        tab,
      });
    }
    trackLifecycleTabViewed({ tab });
  }, [tab]);

  const refreshOpenCount = useCallback(
    () => setCountVersion((value) => value + 1),
    [],
  );

  useEffect(() => {
    let active = true;
    lifecycleAPI
      .listCallTasks({ status: 'open', per_page: 1 })
      .then((res) => {
        if (active) setOpenCount(res.pagination.total);
      })
      .catch(() => {
        if (active) setOpenCount(null);
      });
    return () => {
      active = false;
    };
  }, [countVersion]);

  useEffect(() => {
    if (!copyDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [copyDirty]);

  const changeTab = (next: LifecycleTab) => {
    if (next === tab) return;
    if (copyDirty) setPendingTab(next);
    else setTab(next);
  };

  const context = useMemo(
    () => ({ setCopyDirty, refreshOpenCount }),
    [refreshOpenCount],
  );

  const overview = tab === 'journeys';

  return (
    <LifecycleToastProvider>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          minWidth: 0,
          padding: '24px 24px 48px',
          background: C.page,
          fontFamily: FONT,
          color: C.black,
          minHeight: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 32,
            flexWrap: 'wrap',
          }}
        >
          <Plain
            aria-label={
              overview ? 'Back to GravyWorkers' : 'Back to Lifecycle journeys'
            }
            data-testid="lifecycleBack"
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={
              overview ? 'Back to GravyWorkers' : 'Back to Lifecycle journeys'
            }
            type="button"
            onClick={() =>
              overview
                ? navigate(`/${ROUTES.workersV2}`)
                : changeTab('journeys')
            }
          >
            <Icon d={PATHS.back} size={20} />
          </Plain>
          <h1
            style={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: 1.4,
              margin: 0,
            }}
          >
            Lifecycle journeys
          </h1>
          <nav
            aria-label="More Lifecycle pages"
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              lineHeight: 1.4,
              color: C.grey,
              flexWrap: 'wrap',
            }}
          >
            {SECONDARY.map(({ key, label }, index) => (
              <Fragment key={key}>
                {index > 0 && <span aria-hidden="true">·</span>}
                <Plain
                  aria-current={tab === key ? 'page' : undefined}
                  data-testid={`lifecycleTab-${key}`}
                  style={{
                    color: tab === key ? C.black : C.grey,
                    fontWeight: tab === key ? 700 : 400,
                  }}
                  type="button"
                  onClick={() => changeTab(key)}
                >
                  {label}
                  {key === 'call-tasks' && openCount !== null && (
                    <span data-testid="openCallTaskCount">{` (${openCount})`}</span>
                  )}
                </Plain>
              </Fragment>
            ))}
          </nav>
        </div>
        <LifecycleProvider value={context}>
          {/* Keyed by tab, so a tab that failed to render gets a fresh try
              when the admin switches away and back. */}
          <SectionErrorBoundary key={tab} label={LABELS[tab]}>
            {tab === 'journeys' && <Overview />}
            {tab === 'settings' && <JourneysTab />}
            {tab === 'call-tasks' && <CallTasksTab />}
            {tab === 'copy' && <CopyTab />}
            {tab === 'events' && <EventsTab />}
          </SectionErrorBoundary>
        </LifecycleProvider>
        {(pendingTab || leave.pending) &&
          // The step window is a body-level layer (StepModal, z 9000) while
          // this page sits under the app shell's own stacking context, so a
          // dialog rendered here opened behind the window when a worker link
          // in its Call tasks tab left the page with unsaved copy (review
          // D-04). Rendered at the body too, one layer above the window.
          createPortal(
            <div style={{ position: 'relative', zIndex: 9100 }}>
              <ConfirmationModal
                disableClickout
                title="Discard unsaved changes?"
                message="Your edits to this message are not saved."
                subMessage={null}
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                onCancel={() => {
                  setPendingTab(null);
                  leave.stay();
                }}
                onConfirm={() => {
                  if (pendingTab) {
                    // Already confirmed: back to the Overview is a bare
                    // /lifecycle, which the guard would ask about again.
                    leave.allow(() => setTab(pendingTab));
                    setPendingTab(null);
                  } else {
                    leave.proceed();
                  }
                }}
              />
            </div>,
            document.body,
          )}
      </div>
    </LifecycleToastProvider>
  );
};

export default Lifecycle;
